
// import {throwError as observableThrowError} from 'rxjs';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { JwtService } from './jwt.service';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

@Injectable()
export class ApiService {

  constructor(private http: HttpClient,
    private jwtService: JwtService) { }

  private setHeaders(customJwt?: string, isBearer: boolean = false): HttpHeaders {
    const headersConfig = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Client-Version': '2.0' // Version du client pour bloquer les anciens polling
    };

    // Utiliser le token personnalisé avec format Bearer si fourni
    if (customJwt) {
      const authPrefix = isBearer ? 'Bearer' : 'Token';
      headersConfig['Authorization'] = `${authPrefix} ${customJwt}`;
    } else if (this.jwtService.getToken()) {
      headersConfig['Authorization'] = `Token ${this.jwtService.getToken()}`;
    }
    return new HttpHeaders(headersConfig);
  }

  private formatErrors(error: any) {
    return Observable.throw(error);
  }

  private getApiUrl(useV2: boolean = false): string {
    return useV2 ? environment.api_url_v2 : environment.api_url;
  }

  get(path: string, params: HttpParams = new HttpParams(), useV2: boolean = false, customJwt?: string): Observable<any> {
    return this.http.get(`${this.getApiUrl(useV2)}${path}`, { params: params, headers: this.setHeaders(customJwt, true) })
      .catch(this.formatErrors)
      .map((res: Response) => res);
  }

  put(path: string, body: Object = {}, useV2: boolean = false, customJwt?: string): Observable<any> {
    return this.http.put(
      `${this.getApiUrl(useV2)}${path}`,
      JSON.stringify(body),
      { headers: this.setHeaders(customJwt, true) }
    )
      .catch(this.formatErrors)
      .map((res: Response) => res);
  }

  post(path: string, body: Object = {}, useV2: boolean = false, customJwt?: string): Observable<any> {
    return this.http.post(
      `${this.getApiUrl(useV2)}${path}`,
      JSON.stringify(body),
      { headers: this.setHeaders(customJwt, true) }
    )
      .catch(this.formatErrors)
      .map((res: Response) => res);
  }

  patch(path: string, body: Object = {}, useV2: boolean = false, customJwt?: string): Observable<any> {
    return this.http.patch(
      `${this.getApiUrl(useV2)}${path}`,
      JSON.stringify(body),
      { headers: this.setHeaders(customJwt, true) }
    ).catch(this.formatErrors)
      .map((res: Response) => res);
  }

  post_with_headers_in_resp(path: string, body: Object = {}): Observable<any> {
    return this.http.post(
      `${environment.api_url}${path}`,
      JSON.stringify(body),
      { headers: this.setHeaders() }
    )
      .catch(this.formatErrors)
  }

  delete(path, useV2: boolean = false, customJwt?: string): Observable<any> {
    return this.http.delete(
      `${this.getApiUrl(useV2)}${path}`,
      { headers: this.setHeaders(customJwt, true) }
    )
      .catch(this.formatErrors)
      .map((res: Response) => res);
  }

  getBlobFile(path: string, params: HttpParams = new HttpParams()): Observable<any> {
    const header = new HttpHeaders({
      'Content-Type': 'application/zip',
      'Accept': 'application/zip',
      'Authorization': `Token ${this.jwtService.getToken()}`,
      'Content-Disposition': 'attachment',
    });

    return this.http.get(`${environment.api_url}${path}`, { params: params, headers: header, observe: 'response', responseType: 'arraybuffer' })
      .catch(this.formatErrors)
      .map(response => {
        return response;
      })
  }

  /**
   * GET request pour télécharger un fichier blob (PDF, images, etc.)
   * @param path Chemin relatif de l'endpoint
   * @param useV2 Si true, utilise l'API v2, sinon v1
   * @param customJwt Token personnalisé optionnel
   */
  getBlob(path: string, useV2: boolean = false, customJwt?: string): Observable<any> {
    const headers = this.setHeaders(customJwt, true).set('Accept', 'application/octet-stream');
    
    return this.http.get(`${this.getApiUrl(useV2)}${path}`, { 
      headers: headers, 
      responseType: 'blob',
      observe: 'response'
    })
      .catch(this.formatErrors)
      .map((response: any) => response);
  }

  /**
   * GET request pour télécharger un fichier texte (CSV, etc.)
   * @param path Chemin relatif de l'endpoint
   * @param useV2 Si true, utilise l'API v2, sinon v1
   * @param customJwt Token personnalisé optionnel
   */
  getText(path: string, useV2: boolean = false, customJwt?: string): Observable<any> {
    const headers = this.setHeaders(customJwt, true)
      .set('Accept', 'text/csv')
      .set('Content-Type', 'text/csv');
    
    return this.http.get(`${this.getApiUrl(useV2)}${path}`, { 
      headers: headers, 
      responseType: 'arraybuffer',
      observe: 'response'
    })
      .catch(this.formatErrors)
      .map((response: any) => response);
  }

  /**
   * POST request avec FormData pour upload de fichiers
   * @param path Chemin relatif de l'endpoint
   * @param formData FormData contenant les données à envoyer
   * @param useV2 Si true, utilise l'API v2, sinon v1
   * @param customJwt Token personnalisé optionnel
   */
  postFormData(path: string, formData: FormData, useV2: boolean = false, customJwt?: string): Observable<any> {
    // Ne pas définir Content-Type pour FormData, le navigateur le gère automatiquement avec boundary
    const authPrefix = (useV2 || customJwt) ? 'Bearer' : 'Token';
    const token = customJwt || this.jwtService.getToken();
    
    const headers = new HttpHeaders({
      'Authorization': `${authPrefix} ${token}`,
      'X-Client-Version': '2.0'
    });

    return this.http.post(
      `${this.getApiUrl(useV2)}${path}`,
      formData,
      { headers: headers }
    )
      .catch(this.formatErrors)
      .map((res: Response) => res);
  }

}
